// src/pages/Songs.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

const SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1kUE3UKLbQjQK9UcfDQNZ5larkXYPd1YJ8mtaozwn8Ic/gviz/tq?tqx=out:json&gid=0";

const PAGE_SIZE = 30;

export default function Songs() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0); // 0 = últimas 30 filas
  const navigate = useNavigate();

  // 1) Cargar desde Google Sheets
  useEffect(() => {
    const cargarDesdeSheet = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(SHEET_URL);
        const text = await res.text();

        const match = text.match(
          /google\.visualization\.Query\.setResponse\((.*)\);/s
        );
        if (!match) {
          throw new Error("No se pudo parsear la respuesta de Google Sheets");
        }

        const json = JSON.parse(match[1]);
        const rows = json.table.rows || [];

        const lista = rows.map((row, index) => {
          const c = row.c || [];

          return {
            index, // posición en la hoja
            userId: c[0]?.v || "",
            songId: c[1]?.v || "",
            letra: c[2]?.v || "",
            estilo: c[3]?.v || "",
            vozGen: c[4]?.v || "",
            inst: c[5]?.v || "",
            urgencia: c[6]?.v || "",
            prompt: c[7]?.v || "",
            enlaceCancion: c[8]?.v || "",
            // campos que llenaremos desde Firestore
            taskId: "",
            coverUrl: "",
            coverUrl02: "",
            _detailsLoaded: false,
          };
        });

        setSongs(lista);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    cargarDesdeSheet();
  }, []);

  // 2) Cargar detalles de Firestore SOLO para la página actual
  useEffect(() => {
    const cargarDetallesFirestore = async () => {
      if (!songs.length) return;

      const totalPages = Math.ceil(songs.length / PAGE_SIZE);
      const currentPage = Math.min(page, Math.max(totalPages - 1, 0));

      const start = Math.max(
        songs.length - (currentPage + 1) * PAGE_SIZE,
        0
      ); // índice inicial (desde el final)
      const end = songs.length - currentPage * PAGE_SIZE; // índice final

      const subset = songs.slice(start, end);

      await Promise.all(
        subset.map(async (song) => {
          if (!song.userId || !song.songId) return;
          if (song._detailsLoaded) return;

          try {
            const ref = doc(
              db,
              "usuarios",
              song.userId,
              "canciones",
              song.songId
            );
            const snap = await getDoc(ref);

            if (!snap.exists()) {
              // Marcamos como cargado aunque no exista
              setSongs((prev) => {
                const copy = [...prev];
                const idx = copy.findIndex((s) => s.index === song.index);
                if (idx === -1) return prev;
                copy[idx] = {
                  ...copy[idx],
                  _detailsLoaded: true,
                };
                return copy;
              });
              return;
            }

            const data = snap.data();

            setSongs((prev) => {
              const copy = [...prev];
              const idx = copy.findIndex((s) => s.index === song.index);
              if (idx === -1) return prev;
              copy[idx] = {
                ...copy[idx],
                _detailsLoaded: true,
                taskId: data.taskId || "",
                coverUrl: data.coverUrl || "",
                coverUrl02: data.coverUrl02 || "",
              };
              return copy;
            });
          } catch (err) {
            console.error("Error leyendo canción en Firestore", err);
          }
        })
      );
    };

    cargarDetallesFirestore();
  }, [page, songs]);

  const handleOpenDetail = (song) => {
    if (!song.userId || !song.songId) return;
    navigate(`/songs/${song.userId}/${song.songId}`);
  };

  if (loading) return <p>Cargando canciones desde Google Sheets...</p>;
  if (error) return <p>Error: {error}</p>;

  // Cálculo de página actual (desde el final)
  const totalPages = Math.ceil(songs.length / PAGE_SIZE) || 1;
  const currentPage = Math.min(page, totalPages - 1);

  const start = Math.max(
    songs.length - (currentPage + 1) * PAGE_SIZE,
    0
  );
  const end = songs.length - currentPage * PAGE_SIZE;
  const pageSongs = songs.slice(start, end);

  return (
    <div>
      <h1>Canciones generadas</h1>
      <p style={{ marginBottom: 16 }}>Total filas: {songs.length}</p>

      <div style={{ marginBottom: 8 }}>
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 0))}
          disabled={currentPage === 0}
          style={{ marginRight: 8 }}
        >
          ← Más recientes
        </button>
        <button
          onClick={() =>
            setPage((p) => Math.min(p + 1, totalPages - 1))
          }
          disabled={currentPage >= totalPages - 1}
        >
          Más antiguas →
        </button>
        <span style={{ marginLeft: 12, fontSize: 13 }}>
          Página {currentPage + 1} de {totalPages}
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 14,
          }}
        >
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>ID Usuario</th>
              <th style={th}>Canción ID</th>
              <th style={th}>Estilo</th>
              <th style={th}>Voz</th>
              <th style={th}>Urgencia</th>
              <th style={th}>Prompt</th>
              <th style={th}>Task ID</th>
              <th style={th}>Cover 1</th>
              <th style={th}>Cover 2</th>
            </tr>
          </thead>
          <tbody>
            {pageSongs.map((song, idx) => {
              const rowNumber = start + idx + 1;
              const tieneCover1 = !!song.coverUrl;
              const tieneCover2 = !!song.coverUrl02;

              return (
                <tr
                  key={`${song.index}-${song.userId}-${song.songId}`}
                  style={{
                    cursor:
                      song.userId && song.songId ? "pointer" : "default",
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#e9e9e9", // ⬅️ gris más fuerte

                    }}
                  onClick={() => handleOpenDetail(song)}
                >
                  <td style={td}>{rowNumber}</td>
                  <td style={td}>{song.userId}</td>
                  <td style={td}>{song.songId}</td>
                  <td style={{ ...td, ...clamp2, maxWidth: 160 }}>
                    {song.estilo}
                  </td>
                  <td style={{ ...td, ...clamp2, maxWidth: 120 }}>
                    {song.vozGen}
                  </td>
                  <td style={td}>{song.urgencia}</td>
                  <td style={{ ...td, ...clamp2, maxWidth: 260 }}>
                    {song.prompt}
                  </td>
                  <td style={{ ...td, ...clamp2, maxWidth: 180 }}>
                    {song.taskId || "-"}
                  </td>
                  <td style={td}>{tieneCover1 ? "Sí" : "No"}</td>
                  <td style={td}>{tieneCover2 ? "Sí" : "No"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 8, fontSize: 12, color: "#777" }}>
        * Se muestran {PAGE_SIZE} filas por página, empezando por las
        últimas de la hoja (más recientes).
      </p>
    </div>
  );
}

const th = {
  borderBottom: "1px solid #ddd",
  textAlign: "left",
  padding: "8px",
  backgroundColor: "#f5f5f5",
};

const td = {
  borderBottom: "1px solid #eee",
  padding: "8px",
};

const clamp2 = {
  display: "-webkit-box",
  WebkitLineClamp: 2, // máximo 2 líneas
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};
